import { customElement, observable, GenesisElement } from '@genesislcap/web-core';
import { html, repeat } from '@genesislcap/web-core';
import { css } from '@genesislcap/web-core';
import type { UploadValidationResult, UploadRowResult } from '../../../../mbm/utils/bulk-response-upload/upload-validator';

export const UploadValidationFlyoutStyles = css`
  .upload-validation-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 16px;
    gap: 12px;
  }

  .upload-validation-summary {
    font-size: var(--type-ramp-base-font-size);
    color: var(--neutral-foreground-rest);
    padding: 8px 0;
  }

  .upload-validation-table-wrapper {
    flex: 1;
    min-height: 0;
    max-height: 60vh;
    overflow-y: auto;
    overflow-x: auto;
    border: 1px solid var(--neutral-stroke-divider-rest);
    border-radius: calc(var(--control-corner-radius) * 1px);
  }

  .upload-validation-table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--type-ramp-base-font-size);
  }

  .upload-validation-table th {
    text-align: left;
    padding: 8px 12px;
    background: var(--neutral-fill-layer-rest);
    border-bottom: 1px solid var(--neutral-stroke-divider-rest);
    font-weight: 600;
  }

  .upload-validation-table td {
    padding: 8px 12px;
    border-bottom: 1px solid var(--neutral-stroke-subtle-rest);
  }

  .upload-validation-table tr.row-error {
    background-color: rgba(185, 28, 28, 0.15);
  }

  .upload-validation-table tr.row-error td {
    color: var(--error-fill-rest, #b91c1c);
  }

  .upload-validation-table tr.row-ignored td {
    color: var(--neutral-foreground-hint);
  }

  .upload-validation-actions {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    padding-top: 8px;
    border-top: 1px solid var(--neutral-stroke-divider-rest);
  }
`;

export const UploadValidationFlyoutTemplate = html<UploadValidationFlyout>`
  <div class="upload-validation-container">
    <div class="upload-validation-summary">
      ${(x) =>
        x.result
          ? `${x.result.validRows} valid, ${x.result.errorRows} error(s) (highlighted), ${x.result.ignoredRows} ignored (Reference not found in database)`
          : ''}
    </div>
    <div class="upload-validation-table-wrapper">
      <table class="upload-validation-table">
        <thead>
          <tr>
            <th>Row</th>
            <th>Reference#</th>
            <th>Response</th>
            <th>Response Type</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          ${repeat(
            (x) => x.rowsToShow,
            html`
              <tr class="${(x) => (x.hasValidationError ? 'row-error' : x.existsInGrid ? '' : 'row-ignored')}">
                <td>${(x) => x.rowIndex}</td>
                <td>${(x) => x.BOND_ID}</td>
                <td>${(x) => x.BID_PRICE}</td>
                <td>${(x) => x.BID_PRICE_TYPE}</td>
                <td>
                  ${(x) =>
                    x.hasValidationError
                      ? (x.errorMessage ?? 'Error')
                      : x.existsInGrid
                        ? 'Valid'
                        : 'Ignored'}
                </td>
              </tr>
            `
          )}
        </tbody>
      </table>
    </div>
    <div class="upload-validation-actions">
      <rapid-button appearance="outline" @click=${(x) => x.cancel()}>Cancel</rapid-button>
      <rapid-button
        appearance="primary"
        ?disabled=${(x) => !x.result || x.result.validRows === 0}
        @click=${(x) => x.proceed()}
      >
        Proceed with ${(x) => x.result?.validRows ?? 0} valid row(s)
      </rapid-button>
    </div>
  </div>
`;

@customElement({
  name: 'upload-validation-flyout',
  template: UploadValidationFlyoutTemplate,
  styles: UploadValidationFlyoutStyles,
})
export class UploadValidationFlyout extends GenesisElement {
  @observable result: UploadValidationResult | null = null;

  /** Cached filtered rows (errors + ignored). Updated when result changes. */
  @observable rowsToShow: UploadRowResult[] = [];

  private resolveProceed: ((value: boolean) => void) | null = null;

  open(result: UploadValidationResult): Promise<boolean> {
    this.result = result;
    const rows = result?.rows ?? [];
    this.rowsToShow = rows.filter((r) => r.hasValidationError || !r.existsInGrid);
    return new Promise((resolve) => {
      this.resolveProceed = resolve;
    });
  }

  proceed() {
    if (this.resolveProceed) {
      this.resolveProceed(true);
      this.resolveProceed = null;
    }
  }

  cancel() {
    if (this.resolveProceed) {
      this.resolveProceed(false);
      this.resolveProceed = null;
    }
  }
}

